/**
 * The portfolio: business areas, the projects filed under them, and filing a project under an
 * area with the area's acceptance (mju-ai-workbench docs/plans/2026-09-organisation-projects.md,
 * W1–W3; since 0.9.0 every platform project is the organisation's and "promotion" means filing:
 * docs/plans/2026-10-organisation-projects-and-working-together.md).
 *
 *   GET  /portfolio/v1/areas                      signed-in people: the areas, with who manages them
 *   POST /portfolio/v1/areas                      portfolio managers
 *   PATCH|DELETE /portfolio/v1/areas/:id          portfolio managers, or that area's managers (rename, managers)
 *   GET  /portfolio/v1                            portfolio managers and area managers: areas → projects → roots
 *   POST /portfolio/v1/projects                   an enterprise project in an area (portfolio or area managers)
 *   PUT  /portfolio/v1/projects/:id/area          file a project under another area
 *   POST /projects/v1                             a workbench or a person registers a project (device token allowed)
 *   POST /projects/v1/:id/promote                 an owner asks to file an unfiled project under an area → pending
 *   GET  /portfolio/v1/promotions?status=pending  what waits on the caller (area managers see their areas')
 *   POST /portfolio/v1/promotions/:id/accept      that area's manager, a portfolio manager or an administrator
 *   POST /portfolio/v1/promotions/:id/reject      the same, with a reason; the owner may withdraw with /withdraw
 *
 * Roles: `portfolio-manager` (a tenant role, or any role granted `portfolio:manage`) manages every
 * area; an area's `managers` manage that area. Administrators can do everything either can.
 */
import { z } from "zod";
export const PORTFOLIO_MANAGER_ROLE = "portfolio-manager";
const areaIdSchema = z.string().min(1).max(100).regex(/^[a-z0-9][a-z0-9-]*$/, "lower-case letters, digits and -");
/** A person (Entra object id) or an IdP group who manages an area. */
export const areaManagerSchema = z.object({
    subjectType: z.enum(["user", "group"]),
    subjectId: z.string().min(1).max(200),
});
/**
 * The organisation's portfolio managers, named here rather than through the Entra app role (AIOE, 2 Nov 2026): people
 * and directory groups, so an organisation hands the portfolio to a group instead of assigning people one by one. A
 * person listed, or in a listed group (by their own sign-in's groups), is a portfolio manager exactly as the
 * `portfolio-manager` app role makes them; the app role still counts. Not on Mojo Up AI Cloud (no portfolio).
 *
 *   GET /portfolio/v1/managers   signed-in people (a desktop's token too) → { managers }
 *   PUT /portfolio/v1/managers   administrators, { managers } → { managers }
 */
export const portfolioManagersPutSchema = z.object({ managers: z.array(areaManagerSchema).max(100) });
/** A finance cost code (cost centre, GL code): free text, as the organisation's finance system writes it. "" clears it on a PATCH. */
export const costCodeSchema = z.string().max(64).regex(/^[^\n\r]*$/, "one line");
export const businessAreaSchema = z.object({
    id: areaIdSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    managers: z.array(areaManagerSchema).max(100),
    /** Cost round 2 (AIOE 0.18.0, optional): the code chargeback statements book the area's spend to. A project's own code beats it. */
    costCode: costCodeSchema.optional(),
    createdAt: z.string(),
    createdBy: z.string(),
});
export const businessAreaCreateSchema = z.object({
    id: areaIdSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    managers: z.array(areaManagerSchema).max(100).default([]),
    costCode: costCodeSchema.optional(),
});
/** No defaults here: a field left out stays as it is. */
export const businessAreaPatchSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
    managers: z.array(areaManagerSchema).max(100).optional(),
    /** "" clears it. */
    costCode: costCodeSchema.optional(),
});
export const promotionStatusSchema = z.enum(["pending", "accepted", "rejected", "withdrawn"]);
export const promotionSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    businessAreaId: z.string(),
    status: promotionStatusSchema,
    requestedBy: z.string(),
    requestedAt: z.string(),
    /** Why the owner wants it filed there (shown to the area's managers). */
    note: z.string().max(2000).optional(),
    decidedBy: z.string().optional(),
    decidedAt: z.string().optional(),
    /** The manager's reason when rejecting. */
    reason: z.string().max(2000).optional(),
});
export const promoteRequestSchema = z.object({
    businessAreaId: areaIdSchema,
    note: z.string().max(2000).optional(),
});
export const promotionDecisionSchema = z.object({ reason: z.string().max(2000).optional() });
export const projectAreaPutSchema = z.object({ businessAreaId: areaIdSchema.nullable() });
//# sourceMappingURL=portfolio.js.map